/**
 * SubscriptionHistory — Stream S (Paritaet R4)
 *
 * Vereinheitlichte Premium-Historie auf /account/subscription. Die Sektion ist
 * IMMER sichtbar (auch fuer Free-/Expired-User) und zeigt sonst einen
 * Leerzustand — anders als v1, wo der Tab komplett verschwand.
 *
 * Je Eintrag: Status-Badge (ueber den effektiven Anzeige-Status, Issue #143),
 * Quelle (Stripe/Krypto/Code), Zeitraum (Start → Ablauf bzw. "Nie") und eine
 * gekuerzte Stripe-ID mit Copy-Button.
 *
 * Issue #141: Tabs "Aktiv" (active + expiring) / "Beendet" (alles andere),
 * je mit Zaehler im Label — sonst geht ein einzelnes aktives Abo in einer
 * langen Historie unter. Der Kuendigen-Button je Zeile ist ABSICHTLICH
 * entfernt: die Statuskarte oben (CancelCard in SubscriptionPage.tsx) ist
 * jetzt der einzige Kuendigungsweg, vorher gab es zwei parallele Wege auf
 * ein- und demselben aktiven Abo.
 *
 * Fehler der Historie sind bewusst NICHT blockierend: sie ersetzen nur die
 * Liste, nie die Statuskarte darueber.
 */
import { useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Subscription } from './useSubscription'
import { useSubscriptionHistory, type SubscriptionHistoryItem } from './useSubscriptionHistory'
import {
  formatSubscriptionDate,
  historyDisplayStatus,
  historyStatusBadge,
  historyTabBucket,
  looksLikeProcessorId,
  truncateStripeId,
} from './subscriptionUtils'

type SubscriptionHistoryProps = {
  /**
   * Aktuelles Abo (GET /api/v2/subscription) — Quelle der Hero-Karte.
   *
   * Fix P1 (Abnahme 2026-09-01): die History-Liste (GET /api/v2/subscriptions/history)
   * bildet Krypto-Zahlungen ueber deren Zahlungsfenster ab, nicht ueber das
   * tatsaechliche premium_expires_at — ein per Krypto verlaengertes Abo kann so
   * OHNE aktive Zeile in der History erscheinen, waehrend die Hero-Karte "Active"
   * zeigt (Acceptance: "exactly one Active row"). Statt eines neuen Backend-Feldes
   * wird die bereits geladene aktuelle Subscription als Zeile ergaenzt, sobald die
   * History selbst keinen aktiven Eintrag enthaelt.
   */
  activeSubscription?: Subscription | null
  activePlanLabel?: string | null
}

type HistoryTab = 'active' | 'ended'

export function SubscriptionHistory({
  activeSubscription,
  activePlanLabel,
}: SubscriptionHistoryProps = {}) {
  const { t } = useTranslation('subscription')
  const { items: rawItems, isLoading, isError, refetch } = useSubscriptionHistory()

  const items = useMemo(
    () => withSynthesizedActiveRow(rawItems, activeSubscription, activePlanLabel),
    [rawItems, activeSubscription, activePlanLabel],
  )

  const [activeTab, setActiveTab] = useState<HistoryTab>('active')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = useCallback(async (item: SubscriptionHistoryItem) => {
    if (!item.stripe_id) return
    try {
      await navigator.clipboard?.writeText(item.stripe_id)
      setCopiedId(item.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      /* Clipboard nicht verfuegbar — kein harter Fehler, ID steht im title */
    }
  }, [])

  // Issue #141/#143: Bucket ueber den effektiven Anzeige-Status (nicht das
  // rohe BE-`status`) — sonst landet ein gekuendigtes, aber noch laufendes
  // Abo faelschlich unter "Beendet".
  const activeItems = useMemo(
    () => items.filter((item) => historyTabBucket(historyDisplayStatus(item)) === 'active'),
    [items],
  )
  const endedItems = useMemo(
    () => items.filter((item) => historyTabBucket(historyDisplayStatus(item)) === 'ended'),
    [items],
  )
  const visibleItems = activeTab === 'active' ? activeItems : endedItems

  return (
    <section
      className="card card-pad"
      data-testid="subscription-history"
      aria-labelledby="subscription-history-title"
    >
      <h3 id="subscription-history-title" style={{ margin: '0 0 8px', fontSize: 14 }}>
        {t('history.title')}
      </h3>

      {isLoading && (
        <div
          aria-busy="true"
          aria-label={t('history.loadingAriaLabel')}
          data-testid="subscription-history-loading"
          style={{ padding: '16px 0', color: 'var(--text-subtle)', fontSize: 14 }}
        >
          {t('history.loading')}
        </div>
      )}

      {!isLoading && isError && (
        <div role="alert" data-testid="subscription-history-error" style={{ fontSize: 14 }}>
          <p style={{ marginBottom: 8 }}>{t('history.error')}</p>
          <button
            className="btn btn-ghost"
            onClick={refetch}
            data-testid="subscription-history-retry"
          >
            {t('common:actions.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p
          data-testid="subscription-history-empty"
          style={{ fontSize: 14, color: 'var(--text-subtle)' }}
        >
          {t('history.empty')}
        </p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <>
          <div className="tabs" role="tablist" aria-label={t('history.tabs.ariaLabel')}>
            <button
              type="button"
              role="tab"
              id="subscription-history-tab-active"
              aria-selected={activeTab === 'active'}
              aria-controls="subscription-history-panel-active"
              className={`tab${activeTab === 'active' ? ' is-active' : ''}`}
              data-testid="subscription-history-tab-active"
              onClick={() => setActiveTab('active')}
            >
              {t('history.tabs.active', { count: activeItems.length })}
            </button>
            <button
              type="button"
              role="tab"
              id="subscription-history-tab-ended"
              aria-selected={activeTab === 'ended'}
              aria-controls="subscription-history-panel-ended"
              className={`tab${activeTab === 'ended' ? ' is-active' : ''}`}
              data-testid="subscription-history-tab-ended"
              onClick={() => setActiveTab('ended')}
            >
              {t('history.tabs.ended', { count: endedItems.length })}
            </button>
          </div>

          <div
            role="tabpanel"
            id={
              activeTab === 'active'
                ? 'subscription-history-panel-active'
                : 'subscription-history-panel-ended'
            }
            aria-labelledby={
              activeTab === 'active'
                ? 'subscription-history-tab-active'
                : 'subscription-history-tab-ended'
            }
            style={{ marginTop: 12 }}
          >
            {visibleItems.length === 0 ? (
              <p
                data-testid="subscription-history-tab-empty"
                style={{ fontSize: 13, color: 'var(--text-subtle)', margin: 0 }}
              >
                {activeTab === 'active'
                  ? t('history.tabs.activeEmpty')
                  : t('history.tabs.endedEmpty')}
              </p>
            ) : (
              <ul
                data-testid="subscription-history-list"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                <li
                  className="h-row"
                  aria-hidden="true"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: 'var(--text-subtle)',
                  }}
                >
                  <span>{t('history.columnPlan')}</span>
                  <span>{t('history.columnPeriod')}</span>
                  <span>{t('history.columnMethod')}</span>
                  <span>{t('history.columnStatus')}</span>
                </li>
                {visibleItems.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    isCopied={copiedId === item.id}
                    onCopy={() => void handleCopy(item)}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 8 }}>
          {t('history.footnote')}
        </p>
      )}

      <style>{`
        .h-row {
          display: grid;
          grid-template-columns: 100px 1fr 110px 90px;
          gap: 12px;
          align-items: center;
          padding: 11px 0;
          border-bottom: 1px solid var(--border);
          font-size: 13px;
        }
        .h-row:last-child { border-bottom: 0; }
        @media (max-width: 640px) {
          .h-row { grid-template-columns: 1fr 1fr; row-gap: 4px; }
          .h-row > :nth-child(3) { order: 3; }
          .h-row > :nth-child(4) { order: 4; }
        }
      `}</style>
    </section>
  )
}

/**
 * Ergaenzt eine synthetische "Active"-Zeile aus der aktuellen Subscription,
 * falls die History-Liste selbst keinen aktiven Eintrag enthaelt (siehe
 * Kommentar an `activeSubscription` oben). Quelle wird an der Zahlungsmethode
 * erkannt: Kartendaten vorhanden -> stripe, sonst crypto (Krypto-Premium hat
 * keine payment_method).
 */
function withSynthesizedActiveRow(
  items: SubscriptionHistoryItem[],
  activeSubscription: Subscription | null | undefined,
  activePlanLabel: string | null | undefined,
): SubscriptionHistoryItem[] {
  if (!activeSubscription || activeSubscription.status !== 'active') return items
  if (items.some((item) => item.status === 'active')) return items

  const synthetic: SubscriptionHistoryItem = {
    id: 'current-active',
    subscription_id: null,
    source: activeSubscription.payment_method ? 'stripe' : 'crypto',
    plan_id: activeSubscription.plan_id,
    plan_label: activePlanLabel ?? activeSubscription.plan_id,
    duration_months: null,
    status: 'active',
    raw_status: 'active',
    started_at: null,
    expires_at: activeSubscription.cancel_at_period_end
      ? activeSubscription.current_period_end
      : null,
    current_period_end: activeSubscription.current_period_end,
    stripe_id: null,
    cancel_at_period_end: activeSubscription.cancel_at_period_end,
    cancellable: false,
  }
  return [synthetic, ...items]
}

type HistoryRowProps = {
  item: SubscriptionHistoryItem
  isCopied: boolean
  onCopy: () => void
}

function HistoryRow({ item, isCopied, onCopy }: HistoryRowProps) {
  const { t } = useTranslation('subscription')

  // Issue #143: effektiver Status statt des rohen BE-Status — ein gekuendigtes,
  // aber noch laufendes Abo zeigt hier dieselbe "Expiring"-Pille wie die
  // Hero-Karte, statt weiter als "Active" durchzuschlagen.
  const displayStatus = historyDisplayStatus(item)
  const badge = historyStatusBadge(displayStatus)
  // Unbekannte Stati duerfen nie roh durchschlagen — neutraler Fallback-Text.
  const statusLabel = t(`status.${displayStatus}`, { defaultValue: t('status.unknown') })
  const sourceLabel = t(`history.source.${item.source}`, {
    defaultValue: t('history.source.unknown'),
  })

  // BE liefert bei unbekanntem Stripe-Produkt die rohe Processor-ID als
  // plan_label (z. B. "price_1Tg9..."). Nie roh anzeigen — Fallback auf
  // plan_id, sonst neutraler Platzhalter (Prototyp kennt nur lesbare Namen).
  const rawPlanLabel = item.plan_label
  const cleanPlanLabel = rawPlanLabel && !looksLikeProcessorId(rawPlanLabel) ? rawPlanLabel : null
  const cleanPlanId = item.plan_id && !looksLikeProcessorId(item.plan_id) ? item.plan_id : null
  const planName = cleanPlanLabel || cleanPlanId || t('history.planUnknown')
  const planLine =
    item.duration_months != null
      ? t('history.planLine', { plan: planName, count: item.duration_months })
      : planName

  const startedLabel = item.started_at
    ? formatSubscriptionDate(item.started_at)
    : t('history.unknownDate')
  // expires_at === null bedeutet laut Contract "laeuft nicht aus" (v1-Paritaet).
  const expiresLabel = item.expires_at
    ? formatSubscriptionDate(item.expires_at)
    : t('history.never')

  const periodText = `${startedLabel} → ${expiresLabel}`

  return (
    <li className="h-row" data-testid="subscription-history-item" data-item-id={item.id}>
      <strong data-testid="history-plan" style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
        {planLine}
      </strong>

      <span
        style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere', minWidth: 0 }}
        data-testid="history-period"
      >
        {periodText}
      </span>

      <span
        style={{ color: 'var(--text-muted)' }}
        data-testid="history-source-badge"
        data-source={item.source}
      >
        {sourceLabel}
      </span>

      <span
        className={badge.className}
        data-testid="history-status-badge"
        data-status={displayStatus}
        title={item.raw_status}
      >
        {statusLabel}
      </span>

      {item.stripe_id && (
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: -2,
          }}
        >
          <code
            data-testid="history-stripe-id"
            title={item.stripe_id}
            style={{ fontSize: 11, color: 'var(--text-subtle)' }}
          >
            {truncateStripeId(item.stripe_id)}
          </code>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCopy}
            data-testid="history-copy-id"
            aria-label={t('history.copyId')}
            title={t('history.copyId')}
            style={{ padding: '2px 8px', fontSize: 11 }}
          >
            {isCopied ? t('history.copied') : t('history.copy')}
          </button>
        </div>
      )}
    </li>
  )
}
